import { CTAButton } from "@/components/atoms/PromoBanners/RootBanner";
import { alternateLinks, rootCanonicalUrl } from "@/config/canonical";
import { HeroSection } from "@/containers/HeroSection";
import { useGoBack } from "@/hooks/useGoBack";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { USP, USPType } from "./USP";

interface BaseLandingPageProps {
  heroTitle: string;
  pageTitle: string;
  ctaTitle: string;
  USPs: USPType[];
  onHandleCTA: () => void;
  children: React.ReactNode;
  videoTitle?: string;
  videoDescription?: string;
}

const youtubeLinks = [
  {
    path: "/get-my-players",
    link: "https://www.youtube.com/watch?v=u-3doILagyA",
  },
  {
    path: "/get-sbc-solver",
    link: "https://www.youtube.com/watch?v=rfisW_rIjSI",
  },
  {
    path: "/get-ai-squad-builder",
    link: "https://www.youtube.com/watch?v=Fcz223dRPHA",
  },
];

interface VideoFrameProps {
  title?: string;
  description?: string;
}

const VideoFrame = ({ title, description }: VideoFrameProps) => {
  const { t } = useTranslation("landing");
  const location = useLocation();

  const matched = youtubeLinks.find((item) =>
    location.pathname.startsWith(item.path),
  );

  const src = matched
    ? matched.link.replace("watch?v=", "embed/") + "?rel=0&modestbranding=1"
    : "";

  if (!src) return null;

  return (
    <div className="px-4 w-full pb-8 pt-16">
      <div className="bg-panel-background rounded-2xl p-6 mobile:p-10 flex flex-col touch-device:flex-row touch-device:items-center gap-6 mobile:gap-10">
        {(title || description) && (
          <div className="flex flex-col gap-2 mobile:w-5/12 shrink-0">
            {title && (
              <h3 className="text-white text-lg mobile:text-xl font-bold">
                {title}
              </h3>
            )}
            {description && (
              <p className="text-dark-tertiary text-sm mobile:text-base">
                {description}
              </p>
            )}
          </div>
        )}
        <div className="relative w-full overflow-hidden aspect-video rounded-xl">
          <iframe
            src={src}
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            title={t("video.iframeTitle")}
          />
        </div>
      </div>
    </div>
  );
};

export const BaseLandingPage = ({
  heroTitle,
  pageTitle,
  ctaTitle,
  USPs,
  onHandleCTA,
  children,
  videoTitle,
  videoDescription,
}: BaseLandingPageProps) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Ad and campaign traffic lands straight here with no history behind it.
  const goBack = useGoBack({ fallback: "/" });
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const canonical = rootCanonicalUrl();
  return (
    <div className="bg-panel-base min-h-screen">
      {isTouchDevice ? (
        <Helmet>
          <title>{heroTitle}</title>
          <link rel="canonical" href={canonical} />
          {alternateLinks(canonical)}
        </Helmet>
      ) : (
        <HeroSection
          helmetTitle={heroTitle}
          helmetDescription={""}
          helmetCanonicalLink={canonical}
          back={goBack}
          shouldBeInTheDocumentFlow={true}
        />
      )}
      <main className="flex flex-col justify-center touch-device:items-start mx-auto touch-device:max-w-content mobile:px-12 text-sm mobile:text-base relative">
        <section className="px-4 z-1">
          <h2 className="mt-4 mobile:mt-10 text-left bg-gradient-to-r from-primary-washed-blue via-primary-blue to-primary-washed-blue  bg-clip-text text-transparent mobile:w-150 text-3xl mobile:text-5xl font-bold">
            {pageTitle}
          </h2>
          <section className="flex flex-col py-6 mobile:py-8 gap-y-4 mobile:gap-y-6 mt-6 touch-device:mt-12">
            {USPs.map((usp) => {
              return <USP {...usp} />;
            })}
          </section>
        </section>
        <section className="flex flex-col justify-center z-1">
          <div className={cn("px-4", isTouchDevice && "mx-auto pt-4")}>
            <CTAButton label={ctaTitle} onClick={onHandleCTA} />
          </div>
        </section>
        <VideoFrame title={videoTitle} description={videoDescription} />
        {children}
      </main>
    </div>
  );
};
